import React, { useState } from "react";
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { studentGrade } from "../shared/gradeModel";
import useTripsToday from "../hooks/useTripsToday";
import { createSignOut, findActiveSignOut } from "./signoutData";
import { findTeacherClass } from "../shared/classForStudent";
import "./SignOutForm.css";

const LOCATIONS = ["Bathroom", "Water Fountain", "Nurse", "Library", "Office", "Other"];

const SignOutForm = ({ room }) => {
  const [idNumber, setIdNumber]           = useState("");
  const [location, setLocation]           = useState("");
  const [customLocation, setCustomLocation] = useState("");
  const [error, setError]                 = useState("");
  const [submitting, setSubmitting]       = useState(false);

  const { user, schoolId, teacherName, showTripsCount, allowCustomLocation } = useUser();
  const tripsToday = useTripsToday(showTripsCount ? user?.uid : null);
  // Trips this student has already taken today (from this teacher's history),
  // matched on the typed ID — no extra read needed.
  const idTrips = idNumber.trim() ? (tripsToday.byId[idNumber.trim()] || 0) : 0;

  const classroomName = teacherName
    ? `${teacherName}'s Classroom`
    : "Classroom";

  // Find a student by ID: your own roster first (covers private class-local numbering),
  // then the school directory (shared students added by anyone in your school).
  const findStudent = async (idNumber) => {
    const id = idNumber.trim();
    if (!id) return null;

    const studentsRef = collection(firestore, "students");

    const ownQueries = [
      query(studentsRef, where("studentId", "==", id), where("addedBy", "==", user.uid)),
      query(studentsRef, where("studentId", "==", id), where("teachers", "array-contains", user.uid)),
    ];
    for (const q of ownQueries) {
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].id;
    }

    if (schoolId) {
      // Any non-private student at the school. A missing `shared` field counts as
      // shared (legacy students were already school-deduped); only students
      // explicitly marked private (shared === false) are excluded.
      const snap = await getDocs(query(
        studentsRef,
        where("studentId", "==", id),
        where("schoolId", "==", schoolId),
      ));
      const match = snap.docs.find(d => d.data().shared !== false);
      if (match) return match.id;
    }

    return null;
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    if (e.target.value !== "Other") setCustomLocation("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const studentDocId = await findStudent(idNumber);
      if (!studentDocId) {
        setError("Student not found. Check the ID number and try again.");
        return;
      }

      // Block duplicate sign-outs (school-wide — a student can only be out once)
      const activeSignOut = await findActiveSignOut(studentDocId, schoolId);
      if (activeSignOut) {
        setError(
          `This student is already signed out to ${activeSignOut.location} (${activeSignOut.className}). Sign them back in first.`
        );
        return;
      }

      const studentSnap = await getDoc(doc(firestore, "students", studentDocId));
      const studentData = studentSnap.data();
      if (!studentData) {
        setError("Student data not found.");
        return;
      }

      const finalLocation = location === "Other" && allowCustomLocation ? customLocation : location;

      // The class this teacher has the student in (their own roster class) is
      // shown as "Class" on the school/following boards. Falls back to the first
      // enrolled class for shared students not on this teacher's roster.
      const ownClass = await findTeacherClass(studentData, user.uid);
      const homeroom = ownClass?.className ?? "";

      // Stamp the room the teacher set (optional) so the school board can show
      // where the student left from. Sign-out isn't tied to a class here.
      await createSignOut({
        studentDocumentNumber: studentDocId,
        studentName: studentData.studentName,
        idNumber,
        grade: studentGrade(studentData),
        schoolId: studentData.schoolId ?? schoolId ?? "",
        teacherUID: user.uid,
        teacherName,
        classId: null,
        className: "",
        homeroom,
        room: room ?? "",
        time_out: new Date().toISOString(),
        location: finalLocation,
      });

      setIdNumber("");
      setLocation("");
      setCustomLocation("");
    } catch (err) {
      console.error("Error adding sign-out:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-container">
      <h2>{classroomName}</h2>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form-row-input">
          <input
            type="text"
            placeholder="Student ID Number"
            value={idNumber}
            onChange={(e) => { setIdNumber(e.target.value); setError(""); }}
            required
          />
          <select value={location} onChange={handleLocationChange} required>
            <option value="">Select Location</option>
            {LOCATIONS.map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        {location === "Other" && allowCustomLocation && (
          <div className="custom-location-input">
            <input
              type="text"
              placeholder="Enter destination"
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value)}
              required
            />
          </div>
        )}

        {idTrips > 0 && !error && (
          <p style={{
            margin: "8px 0 0", fontSize: 14, fontWeight: 600,
            color: idTrips >= 3 ? "#b4231f" : "#a35a00",
          }}>
            🔁 Already signed out {idTrips} time{idTrips === 1 ? "" : "s"} today
          </p>
        )}

        {error && (
          <p style={{ color: "var(--color-error)", fontSize: 14, margin: "8px 0 0" }}>
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting}>
          {submitting ? "Signing out…" : "Sign Out"}
        </button>
      </form>
    </div>
  );
};

export default SignOutForm;
